<template>
  <v-dialog
    v-model="dialog"
    max-width="600px"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        :color="buttonColor"
        :prepend-icon="iconOnly ? undefined : 'mdi-email-outline'"
        :icon="iconOnly ? 'mdi-email-outline' : undefined"
        :variant="buttonVariant"
        :size="buttonSize"
        :class="buttonClass"
        v-bind="activatorProps"
      >
        <span v-if="!iconOnly">Contact Us By Email</span>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Contact Us</span>
      </v-card-title>
      <v-card-text>
        <v-container v-if="!isVerified">
          <v-row class="justify-center text-center">
            <v-col cols="12">
              <p class="text-h6">
                Please solve the math problem to proceed.
              </p>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <p class="text-h5">
                {{ captcha.num1 }} + {{ captcha.num2 }} = ?
              </p>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="captchaInput"
                label="Your Answer"
                type="number"
                required
                @keyup.enter="verifyCaptcha"
              />
            </v-col>
            <v-col
              v-if="captchaError"
              cols="12"
            >
              <v-alert
                type="error"
                dense
              >
                {{ captchaError }}
              </v-alert>
            </v-col>
          </v-row>
        </v-container>

        <v-form
          v-else
          ref="formRef"
          @submit.prevent="sendEmail"
        >
          <v-text-field
            v-model="form.from_name"
            label="Name"
            required
          />
          <v-text-field
            v-model="form.from_email"
            label="Email"
            type="email"
            required
          />
          <v-textarea
            v-model="form.message"
            label="Message"
            required
          />
        </v-form>

        <v-alert
          v-if="statusMessage"
          :type="statusType"
          dense
          class="mt-4"
        >
          {{ statusMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="blue darken-1"
          text
          @click="closeDialog"
        >
          Close
        </v-btn>
        <v-btn
          v-if="isVerified"
          color="blue darken-1"
          :loading="isSending"
          @click="sendEmail"
        >
          Send
        </v-btn>
        <v-btn
          v-else
          color="blue darken-1"
          @click="verifyCaptcha"
        >
          Verify
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import emailjs from '@emailjs/browser';

// Props
defineProps({
  iconOnly: {
    type: Boolean,
    default: false
  },
  buttonColor: {
    type: String,
    default: 'primary'
  },
  buttonVariant: {
    type: String,
    default: 'elevated'
  },
  buttonSize: {
    type: String,
    default: 'default'
  },
  buttonClass: {
    type: String,
    default: ''
  }
});

// State
const dialog = ref(false);
const formRef = ref(null);
const form = ref({
  from_name: '',
  from_email: '',
  message: '',
});
const isSending = ref(false);
const statusMessage = ref('');
const statusType = ref('success');
const isVerified = ref(false);
const captcha = ref({ num1: 0, num2: 0, answer: 0 });
const captchaInput = ref('');
const captchaError = ref('');

// Environment Variables
const emailJsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const emailJsServiceID = 'service_6c4r3yi'; // Replace with your Service ID
const emailJsTemplateID = 'template_9fped5f'; // Replace with your Template ID

// Methods
const generateCaptcha = () => {
  captcha.value.num1 = Math.floor(Math.random() * 10) + 1;
  captcha.value.num2 = Math.floor(Math.random() * 10) + 1;
  captcha.value.answer = captcha.value.num1 + captcha.value.num2;
  captchaInput.value = '';
  captchaError.value = '';
};

const verifyCaptcha = () => {
  if (parseInt(captchaInput.value, 10) === captcha.value.answer) {
    isVerified.value = true;
    captchaError.value = '';
  } else {
    captchaError.value = 'Incorrect answer. Please try again.';
    generateCaptcha();
  }
};

const resetForm = () => {
  form.value = { from_name: '', from_email: '', message: '' };
  statusMessage.value = '';
  isVerified.value = false;
  generateCaptcha();
};

const closeDialog = () => {
  dialog.value = false;
  resetForm();
};

const sendEmail = async () => {
  isSending.value = true;
  statusMessage.value = '';

  const templateParams = {
    ...form.value,
  };

  try {
    await emailjs.send(emailJsServiceID, emailJsTemplateID, templateParams, emailJsPublicKey);
    statusMessage.value = 'Message sent successfully!';
    statusType.value = 'success';
    setTimeout(closeDialog, 2000);
  } catch (error) {
    console.error('Failed to send email:', error);
    statusMessage.value = 'Failed to send message. Please try again later.';
    statusType.value = 'error';
  } finally {
    isSending.value = false;
  }
};

onMounted(() => {
  generateCaptcha();
});
</script>